import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Anchor, Badge, Button, Card, Group, Loader, Select, Stack, Tabs, Text, Textarea, TextInput, Title,
  ActionIcon, Alert,
} from '@mantine/core';
import { IconArrowLeft, IconBan, IconLock, IconShieldCheck, IconShieldX, IconTrash, IconUser, IconUserShield } from '@tabler/icons-react';
import { fetchList, createOne, deleteOne } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { useCurrentTenantId } from '@/shared/hooks/useCurrentTenant';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
// useState gia importato sopra
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import * as userApi from '@/features/users/api';
import type { UserProfileRow, UserRow } from '@/features/users/api';

export const Route = createFileRoute('/_app/users/$userId')({ component: UserDetailPage });

function UserDetailPage() {
  const { userId } = useParams({ from: '/_app/users/$userId' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isSuperAdmin = useIsSuperAdmin();
  // currentTenantId retained for future relationships writes; unused here now.
  void useCurrentTenantId();

  const { data, isLoading } = useQuery({
    queryKey: ['user-with-profile', userId],
    queryFn: () => userApi.fetchUserWithProfile(userId),
    enabled: Boolean(userId),
  });
  const user = data?.user as UserRow | null | undefined;
  const profile = data?.profile as UserProfileRow | null | undefined;

  const impersonate = useMutation({
    mutationFn: async () => {
      // BE expects { TargetUserEmail } — see AccountController.cs:1873 (ImpersonateRequest).
      const targetEmail = user?.email;
      if (!targetEmail) throw new Error('Email del target non disponibile');
      return httpRequest<unknown>('/api/account/impersonate', {
        method: 'POST',
        endpointType: 'plain',
        body: { TargetUserEmail: targetEmail },
      });
    },
    onSuccess: () => {
      notifySuccess('Impersonification avviata. Ricarico la pagina con la nuova sessione.');
      // Forziamo un reload completo per applicare il nuovo JWT lato AuthProvider.
      setTimeout(() => window.location.replace('/'), 600);
    },
    onError: (err) => notifyError(err, 'Impersonification fallita'),
  });

  // ── Account form (User / AspNetUsers) ──
  const accountForm = useForm({
    mode: 'controlled',
    initialValues: { email: '', phoneNumber: '' },
  });

  // ── Profile form (UserProfile) ──
  const profileForm = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: '', lastName: '', nickName: '',
      sex: '', taxId: '', birthDate: '', birthCity: '', birthProvince: '',
      fixedPhone: '', mobilePhone: '', contactEmail: '',
      residenceAddress: '', residenceHouseNumber: '', residenceCity: '',
      residenceProvince: '', residenceZIP: '', residenceState: '',
      occupation: '', description: '', userLang: 'it',
    },
  });

  useEffect(() => {
    if (user) accountForm.setValues({ email: user.email ?? '', phoneNumber: user.phoneNumber ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (profile) {
      profileForm.setValues({
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        nickName: profile.nickName ?? '',
        sex: profile.sex ?? '',
        taxId: profile.taxId ?? '',
        birthDate: profile.birthDate ? profile.birthDate.slice(0, 10) : '',
        birthCity: profile.birthCity ?? '',
        birthProvince: profile.birthProvince ?? '',
        fixedPhone: profile.fixedPhone ?? '',
        mobilePhone: profile.mobilePhone ?? '',
        contactEmail: profile.contactEmail ?? '',
        residenceAddress: profile.residenceAddress ?? '',
        residenceHouseNumber: profile.residenceHouseNumber ?? '',
        residenceCity: profile.residenceCity ?? '',
        residenceProvince: profile.residenceProvince ?? '',
        residenceZIP: profile.residenceZIP ?? '',
        residenceState: profile.residenceState ?? '',
        occupation: profile.occupation ?? '',
        description: profile.description ?? '',
        userLang: profile.userLang ?? 'it',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const saveAccount = useMutation({
    mutationFn: (v: typeof accountForm.values) => userApi.updateMe(userId, v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Account aggiornato' });
      qc.invalidateQueries({ queryKey: ['user-with-profile', userId] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore account' }),
  });

  const saveProfile = useMutation({
    mutationFn: (v: typeof profileForm.values) => {
      const attrs = { ...v, birthDate: v.birthDate || undefined };
      if (profile?.id) return userApi.updateUserProfile(profile.id, attrs);
      // Nessun UserProfile esistente: lo creiamo con la relazione su user.
      return userApi.createUserProfile(userId, { ...attrs, contactEmail: attrs.contactEmail || user?.email });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Profilo aggiornato' });
      qc.invalidateQueries({ queryKey: ['user-with-profile', userId] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore profilo' }),
  });

  if (isLoading) return <Loader />;
  if (!user) return <Text c="dimmed">Utente non trovato</Text>;

  const displayName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim()
    || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
    || user.email;

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Anchor component={Link} to="/users" size="sm">
            <Group gap={4}>
              <IconArrowLeft size={14} /> Torna a Utenti
            </Group>
          </Anchor>
          <Title order={1}>{displayName}</Title>
          <Group gap="xs">
            <Badge variant="light">{user.email}</Badge>
            {user.emailConfirmed && <Badge color="green">Email verificata</Badge>}
            {user.lockoutEnabled && <Badge color="red">Bloccato</Badge>}
            {!profile && <Badge color="orange">Profilo mancante</Badge>}
          </Group>
        </div>
        <Group>
          {isSuperAdmin && (
            <Button
              variant="light"
              color="orange"
              leftSection={<IconUserShield size={16} />}
              loading={impersonate.isPending}
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Impersonifica utente',
                  children: (
                    <Text size="sm">
                      Impersonificare {displayName}? Verrai loggato come questo utente.
                    </Text>
                  ),
                  labels: { confirm: 'Impersonifica', cancel: 'Annulla' },
                  confirmProps: { color: 'blue' },
                  onConfirm: () => impersonate.mutate(),
                });
              }}
            >
              Impersonifica
            </Button>
          )}
          <Button variant="light" color="red" onClick={() => void navigate({ to: '/users' })}>
            Elimina
          </Button>
        </Group>
      </Group>
      <Tabs defaultValue="profile">
        <Tabs.List>
          <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>Profilo</Tabs.Tab>
          <Tabs.Tab value="roles" leftSection={<IconShieldCheck size={16} />}>Ruoli</Tabs.Tab>
          <Tabs.Tab value="security" leftSection={<IconLock size={16} />}>Sicurezza</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="profile" pt="md">
          <Stack>
            <Card withBorder padding="lg" radius="md">
              <Title order={4} mb="sm">Account</Title>
              <form onSubmit={accountForm.onSubmit((v) => { void saveAccount.mutate(v); })}>
                <Stack>
                  <TextInput label="Email" disabled {...accountForm.getInputProps('email')} />
                  <TextInput label="Telefono account" {...accountForm.getInputProps('phoneNumber')} />
                  <Group justify="flex-end">
                    <Button type="submit" variant="light" loading={saveAccount.isPending}>
                      Salva account
                    </Button>
                  </Group>
                </Stack>
              </form>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Title order={4} mb="sm">Anagrafica (UserProfile)</Title>
              {!profile && (
                <Text size="sm" c="orange" mb="sm">
                  L&apos;utente non ha una scheda profilo: compila e salva per crearla.
                </Text>
              )}
              <form onSubmit={profileForm.onSubmit((v) => { void saveProfile.mutate(v); })}>
                <Stack>
                  <Group grow>
                    <TextInput label="Nome" {...profileForm.getInputProps('firstName')} />
                    <TextInput label="Cognome" {...profileForm.getInputProps('lastName')} />
                  </Group>
                  <Group grow>
                    <TextInput label="Nickname" {...profileForm.getInputProps('nickName')} />
                    <Select
                      label="Sesso"
                      data={[{ value: 'M', label: 'M' }, { value: 'F', label: 'F' }, { value: 'X', label: 'Altro' }]}
                      clearable
                      {...profileForm.getInputProps('sex')}
                    />
                  </Group>
                  <Group grow>
                    <TextInput label="Codice fiscale" {...profileForm.getInputProps('taxId')} />
                    <TextInput label="Data di nascita" type="date" {...profileForm.getInputProps('birthDate')} />
                  </Group>
                  <Group grow>
                    <TextInput label="Citta' di nascita" {...profileForm.getInputProps('birthCity')} />
                    <TextInput label="Provincia di nascita" {...profileForm.getInputProps('birthProvince')} />
                  </Group>
                  <Group grow>
                    <TextInput label="Telefono fisso" {...profileForm.getInputProps('fixedPhone')} />
                    <TextInput label="Cellulare" {...profileForm.getInputProps('mobilePhone')} />
                  </Group>
                  <TextInput label="Email di contatto" {...profileForm.getInputProps('contactEmail')} />

                  <Title order={5} mt="sm">Residenza</Title>
                  <Group grow>
                    <TextInput label="Indirizzo" {...profileForm.getInputProps('residenceAddress')} />
                    <TextInput label="Civico" maw={120} {...profileForm.getInputProps('residenceHouseNumber')} />
                  </Group>
                  <Group grow>
                    <TextInput label="Citta'" {...profileForm.getInputProps('residenceCity')} />
                    <TextInput label="Provincia" {...profileForm.getInputProps('residenceProvince')} />
                    <TextInput label="CAP" maw={140} {...profileForm.getInputProps('residenceZIP')} />
                  </Group>
                  <TextInput label="Stato" {...profileForm.getInputProps('residenceState')} />

                  <Title order={5} mt="sm">Altro</Title>
                  <Group grow>
                    <TextInput label="Occupazione" {...profileForm.getInputProps('occupation')} />
                    <Select
                      label="Lingua"
                      data={[{ value: 'it', label: 'Italiano' }, { value: 'en', label: 'English' }]}
                      {...profileForm.getInputProps('userLang')}
                    />
                  </Group>
                  <Textarea label="Note" autosize minRows={2} {...profileForm.getInputProps('description')} />

                  <Group justify="flex-end">
                    <Button type="submit" loading={saveProfile.isPending}>
                      {profile ? 'Salva profilo' : 'Crea profilo'}
                    </Button>
                  </Group>
                </Stack>
              </form>
            </Card>
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="roles" pt="md">
          <UserRolesPanel userId={userId} />
        </Tabs.Panel>
        <Tabs.Panel value="security" pt="md">
          <UserSecurityPanel userId={userId} user={user} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof JsonApiHttpError) return err.errors[0]?.detail ?? err.errors[0]?.title ?? err.message;
  if (err instanceof Error) return err.message;
  return 'Errore';
}

interface RoleListItem { type: 'roles'; id: string; name?: string; normalizedName?: string; isFromMaster?: boolean; [k: string]: unknown; }
interface UserRoleItem { type: 'user-roles'; id: string; tenantId?: string; role?: { id: string }; user?: { id: string }; [k: string]: unknown; }

function UserRolesPanel({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const rolesQ = useQuery({
    queryKey: ['all-roles'],
    queryFn: () => fetchList<RoleListItem>('roles', { sort: ['name'], page: { size: 100 } }),
  });

  const userRolesQ = useQuery({
    queryKey: ['user-roles', userId],
    queryFn: () => fetchList<UserRoleItem>('user-roles', {
      filter: { 'user.id': { eq: userId } },
      include: ['role'],
      page: { size: 100 },
    }),
    enabled: Boolean(userId),
  });

  const allRoles = rolesQ.data?.data ?? [];
  const assigned = userRolesQ.data?.data ?? [];
  const assignedRoleIds = new Set(assigned.map((ur) => ur.role?.id).filter(Boolean) as string[]);
  const availableRoles = allRoles.filter((r) => !assignedRoleIds.has(r.id));

  const assign = useMutation({
    mutationFn: (roleId: string) => createOne<UserRoleItem>('user-roles', {
      relationships: {
        user: { data: { type: 'users', id: userId } },
        role: { data: { type: 'roles', id: roleId } },
      },
    }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ruolo assegnato' });
      setSelectedRoleId(null);
      qc.invalidateQueries({ queryKey: ['user-roles', userId] });
    },
    // BE (post-v1.9): l'assegnazione ora funziona anche per non-superadmin con i
    // claim sufficienti. Un 403 residuo = escalation di privilegi rifiutata.
    onError: (err) => {
      const is403 = err instanceof JsonApiHttpError && err.status === 403;
      notifications.show({
        color: 'red',
        title: is403 ? 'Privilegi insufficienti' : 'Errore',
        message: is403
          ? 'Non puoi assegnare un ruolo con più privilegi dei tuoi (escalation rifiutata).'
          : errorMessage(err),
      });
    },
  });

  const unassign = useMutation({
    mutationFn: (userRoleId: string) => deleteOne('user-roles', userRoleId),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ruolo rimosso' });
      qc.invalidateQueries({ queryKey: ['user-roles', userId] });
    },
    onError: (err) => notifications.show({ color: 'red', title: 'Errore', message: errorMessage(err) }),
  });

  return (
    <Stack>
      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Ruoli assegnati</Title>
        {userRolesQ.isLoading ? <Loader /> : assigned.length === 0 ? (
          <Text c="dimmed" size="sm">Nessun ruolo assegnato.</Text>
        ) : (
          <Stack gap="xs">
            {assigned.map((ur) => {
              const role = allRoles.find((r) => r.id === ur.role?.id);
              return (
                <Group key={ur.id} justify="space-between">
                  <Group gap="xs">
                    <Badge variant="light" leftSection={<IconShieldCheck size={12} />}>{role?.name ?? ur.role?.id ?? '—'}</Badge>
                    {role?.isFromMaster && <Badge size="xs" variant="light" color="gray">Sistema</Badge>}
                    {role?.normalizedName && <Text size="xs" c="dimmed">{role.normalizedName}</Text>}
                  </Group>
                  <ActionIcon variant="subtle" color="red" onClick={() => unassign.mutate(ur.id)} aria-label="Rimuovi ruolo">
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              );
            })}
          </Stack>
        )}
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Assegna nuovo ruolo</Title>
        <Group>
          <Select
            placeholder="Seleziona ruolo..."
            data={availableRoles.map((r) => ({ value: r.id, label: r.name ?? r.id }))}
            value={selectedRoleId}
            onChange={setSelectedRoleId}
            searchable
            w={320}
            disabled={availableRoles.length === 0}
          />
          <Button disabled={!selectedRoleId} loading={assign.isPending} onClick={() => selectedRoleId && assign.mutate(selectedRoleId)}>
            Assegna
          </Button>
        </Group>
        {availableRoles.length === 0 && allRoles.length > 0 && (
          <Text size="xs" c="dimmed" mt="xs">Tutti i ruoli disponibili sono gia' assegnati.</Text>
        )}
      </Card>
    </Stack>
  );
}

interface BannedUserItem { type: 'banned-users'; id: string; userId?: string; tenantId?: string; lockEnd?: string | null; lockActive?: boolean; [k: string]: unknown; }

function UserSecurityPanel({ userId, user }: { userId: string; user: UserRow | null | undefined }) {
  const qc = useQueryClient();

  const banQ = useQuery({
    queryKey: ['banned-users', userId],
    queryFn: () => fetchList<BannedUserItem>('banned-users', {
      filter: { 'user.id': { eq: userId } },
      page: { size: 10 },
    }),
    enabled: Boolean(userId),
  });
  const activeBan = (banQ.data?.data ?? []).find((b) => b.lockActive === true);

  const ban = useMutation({
    mutationFn: () => createOne<BannedUserItem>('banned-users', {
      attributes: {
        lockStart: new Date().toISOString(),
        lockEnd: new Date(Date.now() + 100 * 365 * 24 * 3600 * 1000).toISOString(),
        lockActive: true,
        reason: 'Banned da admin',
      } as Record<string, unknown>,
      relationships: {
        user: { data: { type: 'users', id: userId } },
      },
    }),
    onSuccess: () => {
      notifications.show({ color: 'orange', message: 'Utente bannato' });
      qc.invalidateQueries({ queryKey: ['banned-users', userId] });
    },
    onError: (err) => notifications.show({ color: 'red', title: 'Errore', message: errorMessage(err) }),
  });

  const unban = useMutation({
    mutationFn: (id: string) => deleteOne('banned-users', id),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ban rimosso' });
      qc.invalidateQueries({ queryKey: ['banned-users', userId] });
    },
    onError: (err) => notifications.show({ color: 'red', title: 'Errore', message: errorMessage(err) }),
  });

  return (
    <Stack>
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" mb="sm">
          <Title order={4}>Stato account {user?.email ? `— ${user.email}` : ''}</Title>
          {activeBan ? <Badge color="red" leftSection={<IconShieldX size={12} />}>Bannato</Badge> : <Badge color="green" leftSection={<IconShieldCheck size={12} />}>Attivo</Badge>}
        </Group>
        {activeBan ? (
          <Stack gap="xs">
            <Alert color="orange" variant="light">
              Utente bannato {activeBan.lockEnd ? `fino al ${new Date(activeBan.lockEnd).toLocaleDateString('it-IT')}` : 'a tempo indeterminato'}.
            </Alert>
            <Group justify="flex-end">
              <Button color="green" variant="light" onClick={() => unban.mutate(activeBan.id)} loading={unban.isPending}>
                Rimuovi ban
              </Button>
            </Group>
          </Stack>
        ) : (
          <Group justify="flex-end">
            <Button
              color="red"
              leftSection={<IconBan size={16} />}
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Banna utente',
                  children: <Text size="sm">Confermi il ban di questo utente?</Text>,
                  labels: { confirm: 'Banna', cancel: 'Annulla' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => ban.mutate(),
                });
              }}
              loading={ban.isPending}
            >
              Banna utente
            </Button>
          </Group>
        )}
      </Card>
    </Stack>
  );
}
